import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth";
import { Navigate, useLocation } from "react-router";
import { setAuthTokenProvider } from "../../../shared/api-client";
import { auth, firebaseConfigured } from "./firebase";
import styles from "./auth.module.css";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  configured: boolean;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(
    () => auth?.currentUser ?? null,
  );
  const [loading, setLoading] = useState(firebaseConfigured);

  useEffect(() => {
    if (!auth) {
      setAuthTokenProvider(null);
      setLoading(false);
      return undefined;
    }

    const authInstance = auth;
    setAuthTokenProvider(
      async () => authInstance.currentUser?.getIdToken() ?? null,
    );
    const unsubscribe = onAuthStateChanged(authInstance, (nextUser) => {
      setUser(nextUser);
      setLoading(false);
    });
    return () => {
      unsubscribe();
      setAuthTokenProvider(null);
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      configured: firebaseConfigured,
      async signIn(email, password) {
        if (!auth) throw new Error("Firebase Auth is not configured");
        await signInWithEmailAndPassword(auth, email, password);
      },
      async signOut() {
        if (!auth) return;
        await firebaseSignOut(auth);
      },
    }),
    [loading, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}

export function useOptionalAuth() {
  return useContext(AuthContext);
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className={styles.gate}>
        <div className={styles.message}>Loading</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
